import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, AlertCircle, User, Calendar, MessageCircle, ThumbsUp, Flag, Edit, Trash2 } from 'lucide-react';
import { GuruConnectApi } from '../../services/guruConnectApi';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { exampleReviews, ReviewWithUser } from '../../data/example/reviewData';

interface ReviewSystemProps {
  guruId: string
  currentUserId?: string
  userRole: 'client' | 'guru' | 'admin'
  onReviewSubmitted?: () => void
}

export const ReviewSystem: React.FC<ReviewSystemProps> = ({
  guruId,
  currentUserId,
  userRole,
  onReviewSubmitted
}) => {
  const { getReviews, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('reviews');
  const [reviews, setReviews] = useState<ReviewWithUser[]>([]);
  const [userSessions, setUserSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });

  useEffect(() => {
    loadReviews()
    if (currentUserId) {
      loadUserSessions()
    }
  }, [guruId])

  const loadReviews = async () => {
    try {
      setLoading(true)
      setError('')
      // Load data from centralized example data when in demo mode
      if (isExampleData) {
        const mockReviews = getReviews();
        setReviews(mockReviews);
        calculateStats(mockReviews);
      } else {
        // In a real app, you would have an API endpoint to get reviews for a specific guru
        setReviews([]);
        calculateStats([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reviews')
    } finally {
      setLoading(false)
    }
  }

  const loadUserSessions = async () => {
    try {
      // In a real app, you would load the user's sessions with this guru
      const sessions = await GuruConnectApi.getUserSessions('client')
      const guruSessions = sessions.filter(s => s.guru_id === guruId)
      setUserSessions(guruSessions)
    } catch (err) {
      console.error('Failed to load user sessions:', err)
    }
  }

  const calculateStats = (reviewList: ReviewWithUser[]) => {
    const total = reviewList.length
    const average = total > 0 ? reviewList.reduce((sum, review) => sum + review.rating, 0) / total : 0
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
    
    reviewList.forEach(review => {
      distribution[review.rating as keyof typeof distribution]++
    })

    setStats({
      totalReviews: total,
      averageRating: average,
      ratingDistribution: distribution
    })
  }

  const submitReview = async () => {
    if (!currentUserId || !rating || !reviewText.trim()) {
      setError('Please fill in all required fields')
      return
    }

    try {
      setSubmitting(true)
      setError('')
      
      const reviewData = {
        guru_id: guruId,
        session_id: sessionId || undefined,
        rating,
        review_text: reviewText.trim()
      }

      const newReview = await GuruConnectApi.createReview(reviewData)
      
      // Add the new review to the list
      const reviewWithUser: ReviewWithUser = {
        ...newReview,
        reviewer_name: 'You',
        reviewer_avatar: 'https://ui-avatars.com/api/?name=You&background=6366f1&color=fff&size=128',
        formatted_date: 'Just now'
      }

      setReviews(prev => [reviewWithUser, ...prev])
      calculateStats([reviewWithUser, ...reviews])
      
      // Reset form
      setRating(5)
      setReviewText('')
      setSessionId('')
      setShowReviewForm(false)
      
      onReviewSubmitted?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review')
    } finally {
      setSubmitting(false)
    }
  }

  const renderStars = (rating: number, interactive = false, onStarClick?: (star: number) => void) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => interactive && onStarClick?.(star)}
            disabled={!interactive}
            className={`${
              interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''
            }`}
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
    )
  }

  const getRatingPercentage = (rating: number) => {
    if (stats.totalReviews === 0) return 0
    return Math.round((stats.ratingDistribution[rating as keyof typeof stats.ratingDistribution] / stats.totalReviews) * 100)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading reviews...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Reviews"
          message="These are example reviews for demonstration. Real reviews will be available when users submit feedback."
          className="mb-4"
        />
      )}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Reviews & Ratings</h2>
          {isExampleData && <MockBadge variant="small" />}
        </div>
        
        {userRole === 'client' && currentUserId && (
          <button
            onClick={() => setShowReviewForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            Write a Review
          </button>
        )}
      </div>

      {/* Stats Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Average Rating */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {stats.averageRating.toFixed(1)}
            </div>
            <div className="flex justify-center mb-2">
              {renderStars(Math.round(stats.averageRating))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Rating Breakdown</h3>
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400 w-8">{star}★</span>
                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${getRatingPercentage(star)}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                  {stats.ratingDistribution[star as keyof typeof stats.ratingDistribution]}
                </span>
              </div>
            ))}
          </div>

          {/* Verified Reviews */}
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {reviews.filter(r => r.is_verified).length}
            </div>
            <div className="flex items-center justify-center space-x-1 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Verified Reviews</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round((reviews.filter(r => r.is_verified).length / reviews.length) * 100)}% verified
            </p>
          </div>
        </div>
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Write a Review</h3>
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rating *
              </label>
              <div className="flex items-center space-x-2">
                {renderStars(rating, true, setRating)}
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">{rating} stars</span>
              </div>
            </div>

            {/* Session Selection */}
            {userSessions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Session (Optional)
                </label>
                <select
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a session</option>
                  {userSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.title} - {new Date(session.scheduled_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Review Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Review *
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                placeholder="Share your experience with this health professional..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowReviewForm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No reviews yet</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Be the first to share your experience with this health professional.
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-start space-x-4">
                <img
                  src={review.reviewer_avatar}
                  alt={review.reviewer_name}
                  className="w-12 h-12 rounded-full"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {review.reviewer_name}
                      </h4>
                      {review.is_verified && (
                        <CheckCircle className="w-4 h-4 text-green-600" title="Verified Review" />
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {renderStars(review.rating)}
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {review.formatted_date}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-3">{review.review_text}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                    <button className="flex items-center space-x-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <ThumbsUp className="w-4 h-4" />
                      <span>Helpful</span>
                    </button>
                    <button className="flex items-center space-x-1 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                      <Flag className="w-4 h-4" />
                      <span>Report</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
} 